import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  H3,
  ProgressBar,
  Text,
} from '@bigcommerce/big-design';
import { CheckIcon } from '@bigcommerce/big-design-icons';

import { useAdminBadges } from '@/hooks/useAdminBadges';

interface SetupStep {
  id: string;
  title: string;
  description: string;
  complete: boolean;
  tabId: string;
}

interface SetupChecklistProps {
  onNavigate: (tabId: string) => void;
}

/**
 * Setup checklist component for the Dashboard
 * Shows progress through initial POS setup steps
 */
export function SetupChecklist({ onNavigate }: SetupChecklistProps) {
  const { badges, isLoading } = useAdminBadges();

  // Define setup steps based on badge state
  const steps: SetupStep[] = [
    {
      id: 'channel',
      title: 'POS Channel',
      description: 'Set up a dedicated POS sales channel',
      complete: badges.settings === 0, // 0 means not using channel 1
      tabId: 'settings',
    },
    {
      id: 'location',
      title: 'Store Location',
      description: 'Configure at least one pickup location',
      complete: badges.stores === 0,
      tabId: 'stores',
    },
    {
      id: 'staff',
      title: 'Staff Members',
      description: 'Add staff who will use the POS',
      complete: badges.staff === 0,
      tabId: 'staff',
    },
    {
      id: 'devices',
      title: 'Register Device',
      description: 'Pair at least one device',
      complete: badges.devices === 0,
      tabId: 'devices',
    },
    {
      id: 'payments',
      title: 'Payments',
      description: 'Configure a payment provider',
      complete: badges.payments === 0,
      tabId: 'payments',
    },
  ];

  const completedCount = steps.filter(s => s.complete).length;
  const totalSteps = steps.length;
  const progress = Math.round((completedCount / totalSteps) * 100);
  const isComplete = completedCount === totalSteps;

  // Don't show checklist if setup is complete
  if (isComplete || isLoading) {
    return null;
  }

  return (
    <Box
      backgroundColor="white"
      padding="large"
      borderRadius="normal"
      shadow="raised"
      marginBottom="large"
    >
      <Flex justifyContent="space-between" alignItems="center" marginBottom="medium">
        <H3 marginBottom="none">Setup Progress</H3>
        <Badge
          variant={progress >= 80 ? 'success' : progress >= 40 ? 'warning' : 'secondary'}
          label={`${completedCount}/${totalSteps} complete`}
        />
      </Flex>

      <ProgressBar percent={progress} />

      <Grid gridColumns="repeat(auto-fit, minmax(200px, 1fr))" gridGap="medium" marginTop="large">
        {steps.map((step) => (
          <SetupStepCard
            key={step.id}
            step={step}
            onNavigate={onNavigate}
          />
        ))}
      </Grid>
    </Box>
  );
}

interface SetupStepCardProps {
  step: SetupStep;
  onNavigate: (tabId: string) => void;
}

function SetupStepCard({ step, onNavigate }: SetupStepCardProps) {
  return (
    <Box
      backgroundColor={step.complete ? 'success10' : 'secondary10'}
      padding="medium"
      borderRadius="normal"
      style={{
        border: step.complete ? '1px solid #4caf50' : '1px solid #e0e0e0',
        cursor: step.complete ? 'default' : 'pointer',
      }}
      onClick={() => !step.complete && onNavigate(step.tabId)}
    >
      <Flex alignItems="flex-start">
        <Box
          marginRight="small"
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: step.complete ? '#4caf50' : '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {step.complete && <CheckIcon color="white" size="small" />}
        </Box>
        <Box>
          <Text bold marginBottom="xxSmall">
            {step.title}
          </Text>
          <Text color="secondary60" style={{ fontSize: '0.85rem' }}>
            {step.description}
          </Text>
          {!step.complete && (
            <Button
              variant="subtle"
              marginTop="xSmall"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(step.tabId);
              }}
            >
              Set up
            </Button>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
